/**
 * The documents a plan is made from (docs/plans/document-to-plan-pipeline.md): uploaded, decomposed in rounds of
 * questions and answers, then routed into a plan. A document whose job runs is polled until it ends; the backend says
 * what each status means, nothing here decides it.
 */
import { create } from 'zustand';
import { client, errorText } from '@/lib/api';
import type { document } from '@/lib/client';

/** How often a document whose job runs is read again. */
export const POLL_MS = 3000;

export const WORKING: ReadonlySet<document.DocumentStatus> = new Set(['decomposing', 'planning']);

type Busy = 'upload' | 'answer' | 'plan';

interface DocumentsState {
  recent: document.DocumentSummary[];
  byId: Record<string, document.DocumentView>;
  busy: Partial<Record<Busy, boolean>>;
  /** The page's: a load, an answer or a plan that failed; a load that succeeds clears it. */
  error: string | null;
  /** The same by document, for a card in the chat that shows only its own. */
  errorById: Record<string, string>;
  /** The upload's own, shown by the upload. */
  uploadError: string | null;
  /** The answers being written, by document and round (`<id>:<round>`): a round that fails and comes back keeps them. */
  drafts: Record<string, Record<string, string>>;
  setDraft(id: string, round: number, given: Record<string, string>): void;
  loadRecent(): Promise<void>;
  load(id: string): Promise<document.DocumentView | null>;
  /** The file read and sent (attached in the chat: with its conversation); the new document's id, or null when it was refused. */
  upload(file: File, conversationId?: string): Promise<string | null>;
  answer(id: string, answers: document.GivenAnswer[]): Promise<void>;
  /** The plan made, with the answers at hand taken as decided. */
  plan(id: string, answers: document.GivenAnswer[]): Promise<void>;
  /** The page's error cleared, and the document's own when named. */
  clearError(id?: string): void;
}

const message = errorText;

/** A file's bytes as base64, in chunks (a large PDF overflows one `String.fromCharCode` call). */
async function base64Of(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

export const useDocumentsStore = create<DocumentsState>((set, get) => {
  const keep = (doc: document.DocumentView): void => {
    set((s) => ({ byId: { ...s.byId, [doc.id]: doc } }));
    // A follow-up's request: the one it replaced, if shown, learns it (its card stops taking answers).
    if (doc.supersedes !== null && get().byId[doc.supersedes]?.superseded_by === null) void get().load(doc.supersedes);
  };
  const busyWith = async (what: Busy, id: string, work: () => Promise<void>): Promise<void> => {
    set((s) => {
      const errorById = { ...s.errorById };
      delete errorById[id];
      return { busy: { ...s.busy, [what]: true }, error: null, errorById };
    });
    try {
      await work();
    } catch (err) {
      set((s) => ({ error: message(err), errorById: { ...s.errorById, [id]: message(err) } }));
      // Refused (replaced, busy, planned meanwhile): read it again so the card shows where it is.
      void get().load(id);
    } finally {
      set((s) => ({ busy: { ...s.busy, [what]: false } }));
    }
  };
  return {
    recent: [],
    byId: {},
    busy: {},
    error: null,
    errorById: {},
    uploadError: null,
    drafts: {},

    setDraft(id, round, given) {
      set((s) => ({ drafts: { ...s.drafts, [`${id}:${round}`]: given } }));
    },

    async loadRecent() {
      try {
        const { documents } = await client.plans.listDocuments({ limit: 10 });
        set({ recent: documents });
      } catch {
        // The list is a shortcut: the tab works without it.
      }
    },

    async load(id) {
      try {
        const { document: doc } = await client.plans.getDocument(id);
        keep(doc);
        set({ error: null });
        return doc;
      } catch (err) {
        set({ error: message(err) });
        return null;
      }
    },

    async upload(file, conversationId) {
      set((s) => ({ busy: { ...s.busy, upload: true }, uploadError: null }));
      try {
        const { document: doc } = await client.plans.uploadDocument({ filename: file.name, content_base64: await base64Of(file), ...(conversationId ? { conversation_id: conversationId } : {}) });
        keep(doc);
        return doc.id;
      } catch (err) {
        set({ uploadError: message(err) });
        return null;
      } finally {
        set((s) => ({ busy: { ...s.busy, upload: false } }));
      }
    },

    async answer(id, answers) {
      await busyWith('answer', id, async () => keep((await client.plans.answerDocument(id, { answers })).document));
    },

    async plan(id, answers) {
      await busyWith('plan', id, async () => keep((await client.plans.planFromDocument(id, { answers })).document));
    },

    clearError(id) {
      set((s) => {
        if (id === undefined) return { error: null };
        const errorById = { ...s.errorById };
        delete errorById[id];
        return { error: null, errorById };
      });
    },
  };
});
